import React, { useMemo } from "react";
import { Activity, Heart, Thermometer, Droplet } from "lucide-react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import {
  VitalsTabProps,
  VitalSign,
} from "../../../../../interfaces/screens/content/patientDetails";

// Move static chart options outside component
const baseChartOptions: ApexOptions = {
  chart: {
    type: "area",
    toolbar: {
      show: false,
    },
    animations: {
      enabled: true,
      speed: 800,
    },
    fontFamily: "Inter, sans-serif",
  },
  stroke: {
    curve: "smooth",
    width: 2,
  },
  fill: {
    type: "solid",
    opacity: 0.2,
  },
  grid: {
    show: true,
    borderColor: "#f1f1f1",
    strokeDashArray: 0,
    position: "back",
    xaxis: {
      lines: {
        show: true,
      },
    },
    yaxis: {
      lines: {
        show: true,
      },
    },
    padding: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  },
  xaxis: {
    type: "datetime",
    labels: {
      style: {
        colors: "#666",
        fontSize: "12px",
        fontFamily: "Inter, sans-serif",
      },
      datetimeFormatter: {
        year: "yyyy",
        month: "MMM 'yy",
        day: "dd MMM",
        hour: "HH:mm",
      },
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: "#666",
        fontSize: "12px",
        fontFamily: "Inter, sans-serif",
      },
    },
  },
  tooltip: {
    x: {
      format: "dd MMM yyyy HH:mm",
    },
    theme: "light",
    style: {
      fontSize: "12px",
      fontFamily: "Inter, sans-serif",
    },
  },
  markers: {
    size: 4,
    strokeWidth: 0,
    hover: {
      size: 6,
    },
  },
  legend: {
    position: "top",
    horizontalAlign: "right",
    fontSize: "12px",
    fontFamily: "Inter, sans-serif",
    markers: {
      size: 12,
      strokeWidth: 0,
    },
    itemMargin: {
      horizontal: 8,
      vertical: 0,
    },
  },
};

// Dark mode base options
const darkModeBaseOptions: ApexOptions = {
  ...baseChartOptions,
  grid: {
    ...baseChartOptions.grid,
    borderColor: "#cccccc2f",
  },
  xaxis: {
    ...baseChartOptions.xaxis,
    labels: {
      ...baseChartOptions.xaxis?.labels,
      style: {
        colors: "#999",
        fontSize: "12px",
        fontFamily: "Inter, sans-serif",
      },
    },
  },
  yaxis: {
    ...baseChartOptions.yaxis,
    labels: {
      style: {
        colors: "#999",
        fontSize: "12px",
        fontFamily: "Inter, sans-serif",
      },
    },
  },
  tooltip: {
    ...baseChartOptions.tooltip,
    theme: "dark",
  },
};

export const VitalsTab: React.FC<VitalsTabProps> = React.memo(
  ({ vitalSignsData }) => {
    // Memoize chart options to prevent unnecessary recalculations
    const chartOptions = useMemo(
      () => ({
        heartRate: {
          light: {
            ...baseChartOptions,
            colors: ["#ef4444"],
            grid: { borderColor: "#cccccc2f" },
            yaxis: {
              min: 40,
              max: 120,
              labels: {
                formatter: (value: number) => `${value} bpm`,
              },
            },
          },
          dark: {
            ...darkModeBaseOptions,
            colors: ["#ef4444"],
            yaxis: {
              min: 40,
              max: 120,
              labels: {
                formatter: (value: number) => `${value} bpm`,
              },
            },
          },
        },
        oxygen: {
          light: {
            ...baseChartOptions,
            colors: ["#3b82f6"],
            grid: { borderColor: "#cccccc2f" },
            yaxis: {
              min: 90,
              max: 100,
              labels: {
                formatter: (value: number) => `${value}%`,
              },
            },
          },
          dark: {
            ...darkModeBaseOptions,
            colors: ["#3b82f6"],
            yaxis: {
              min: 90,
              max: 100,
              labels: {
                formatter: (value: number) => `${value}%`,
              },
            },
          },
        },
        bloodPressure: {
          light: {
            ...baseChartOptions,
            colors: ["#8b5cf6", "#ec4899"],
            yaxis: {
              min: 60,
              max: 180,
              labels: {
                formatter: (value: number) => `${value} mmHg`,
              },
            },
          },
          dark: {
            ...darkModeBaseOptions,
            colors: ["#8b5cf6", "#ec4899"],
            yaxis: {
              min: 60,
              max: 180,
              labels: {
                formatter: (value: number) => `${value} mmHg`,
              },
            },
          },
        },
        temperature: {
          light: {
            ...baseChartOptions,
            colors: ["#f97316"],
            yaxis: {
              min: 35,
              max: 42,
              labels: {
                formatter: (value: number) => `${value}°C`,
              },
            },
          },
          dark: {
            ...darkModeBaseOptions,
            colors: ["#f97316"],
            yaxis: {
              min: 35,
              max: 42,
              labels: {
                formatter: (value: number) => `${value}°C`,
              },
            },
          },
        },
      }),
      [],
    );

    const getLatestValue = (data: VitalSign[]) => data[data.length - 1]?.value;

    return (
      <div className="space-y-6">
        {/* Critical Alerts */}
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4">
          <div className="pb-3">
            <h4 className="text-sm text-red-800 dark:text-red-300 font-medium">
              Critical Alerts
            </h4>
          </div>
          <div className="space-y-2">
            <div className="text-sm text-red-700 dark:text-red-300">
              <div className="font-medium">WCC Elevation</div>
              <div className="text-xs">Monitor for signs of infection</div>
            </div>
          </div>
        </div>

        {/* Current Vitals Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-4">
            <div className="flex items-center justify-between pb-2">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Heart Rate
              </h3>
              <Heart className="h-4 w-4 text-red-500" />
            </div>
            <div className="text-2xl font-medium text-red-600 dark:text-red-400">
              {getLatestValue(vitalSignsData.heartRate)} bpm
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Last updated:{" "}
              {new Date(
                vitalSignsData.heartRate[
                  vitalSignsData.heartRate.length - 1
                ]?.timestamp,
              ).toLocaleTimeString()}
            </p>
          </div>

          <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-4">
            <div className="flex items-center justify-between pb-2">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                O₂ Saturation
              </h3>
              <Activity className="h-4 w-4 text-blue-500" />
            </div>
            <div className="text-2xl font-medium text-blue-600 dark:text-blue-400">
              {getLatestValue(vitalSignsData.oxygenSaturation)}%
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Last updated:{" "}
              {new Date(
                vitalSignsData.oxygenSaturation[
                  vitalSignsData.oxygenSaturation.length - 1
                ]?.timestamp,
              ).toLocaleTimeString()}
            </p>
          </div>

          <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-4">
            <div className="flex items-center justify-between pb-2">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Blood Pressure
              </h3>
              <Droplet className="h-4 w-4 text-purple-500" />
            </div>
            <div className="text-2xl font-medium text-purple-600 dark:text-purple-400">
              {getLatestValue(vitalSignsData.bloodPressure.systolic)}/
              {getLatestValue(vitalSignsData.bloodPressure.diastolic)} mmHg
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Last updated:{" "}
              {new Date(
                vitalSignsData.bloodPressure.systolic[
                  vitalSignsData.bloodPressure.systolic.length - 1
                ]?.timestamp,
              ).toLocaleTimeString()}
            </p>
          </div>

          <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-4">
            <div className="flex items-center justify-between pb-2">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Temperature
              </h3>
              <Thermometer className="h-4 w-4 text-orange-500" />
            </div>
            <div className="text-2xl font-medium text-orange-600 dark:text-orange-400">
              {getLatestValue(vitalSignsData.temperature)}°C
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Last updated:{" "}
              {new Date(
                vitalSignsData.temperature[
                  vitalSignsData.temperature.length - 1
                ]?.timestamp,
              ).toLocaleTimeString()}
            </p>
          </div>
        </div>

        {/* Vital Signs Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Heart Rate Chart */}
          <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6">
            <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-white">
              Heart Rate Trend
            </h3>
            <ReactApexChart
              options={chartOptions.heartRate.dark}
              series={[
                {
                  name: "Heart Rate",
                  data: vitalSignsData.heartRate.map((vital) => ({
                    x: new Date(vital.timestamp).getTime(),
                    y: vital.value,
                  })),
                },
              ]}
              type="area"
              height={300}
            />
          </div>

          {/* Oxygen Saturation Chart */}
          <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6">
            <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-white">
              Oxygen Saturation Trend
            </h3>
            <ReactApexChart
              options={chartOptions.oxygen.dark}
              series={[
                {
                  name: "O₂ Saturation",
                  data: vitalSignsData.oxygenSaturation.map((vital) => ({
                    x: new Date(vital.timestamp).getTime(),
                    y: vital.value,
                  })),
                },
              ]}
              type="area"
              height={300}
            />
          </div>

          {/* Blood Pressure Chart */}
          <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6">
            <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-white">
              Blood Pressure Trend
            </h3>
            <ReactApexChart
              options={chartOptions.bloodPressure.dark}
              series={[
                {
                  name: "Systolic",
                  data: vitalSignsData.bloodPressure.systolic.map((vital) => ({
                    x: new Date(vital.timestamp).getTime(),
                    y: vital.value,
                  })),
                },
                {
                  name: "Diastolic",
                  data: vitalSignsData.bloodPressure.diastolic.map((vital) => ({
                    x: new Date(vital.timestamp).getTime(),
                    y: vital.value,
                  })),
                },
              ]}
              type="area"
              height={300}
            />
          </div>

          {/* Temperature Chart */}
          <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-darkCardBackground p-6">
            <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-white">
              Temperature Trend
            </h3>
            <ReactApexChart
              options={chartOptions.temperature.dark}
              series={[
                {
                  name: "Temperature",
                  data: vitalSignsData.temperature.map((vital) => ({
                    x: new Date(vital.timestamp).getTime(),
                    y: vital.value,
                  })),
                },
              ]}
              type="area"
              height={300}
            />
          </div>
        </div>
      </div>
    );
  },
);

VitalsTab.displayName = "VitalsTab";
